import { useMemo, useRef, useState } from 'react'
import { GoogleMap, LoadScript, MarkerF, InfoWindowF } from '@react-google-maps/api'
import { Box, Grid, Stack, Typography } from '@mui/material'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'

type LocationPoint = {
    id?: string | number
    lat: number | string
    lng: number | string
    title?: string
    img?: string
    visitedAt?: string,
    link?: string
}

type CommonMapProps = {
    locations: LocationPoint[]
}

export default function CommonMap({ locations }: CommonMapProps) {
    const [activePoint, setActivePoint] = useState<LocationPoint | null>(null)
    const ignoreNextMapClickRef = useRef(false)

    const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: 0, lng: 0 })
    const zoom = 1

    const points = useMemo(() => {
        return (locations ?? [])
            .map((p) => ({
                ...p,
                lat: typeof p.lat === 'string' ? parseFloat(p.lat) : p.lat,
                lng: typeof p.lng === 'string' ? parseFloat(p.lng) : p.lng,
            }))
            .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))
    }, [locations])

    return (
        <Stack spacing={2} direction="row">
            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {points.length ? (
                    <LoadScript googleMapsApiKey={process.env.REACT_APP_FIREBASE_API_KEY || ''}>
                        <Grid item md={12} sx={{ height: '100%' }}>
                            <Box sx={{ width: '100%', height: '100%', minHeight: 250 }}>
                                <GoogleMap
                                    mapContainerStyle={{ width: 450, height: 300 }}
                                    center={mapCenter}
                                    zoom={zoom}
                                    options={{
                                        minZoom: 1,
                                        maxZoom: 20,
                                        clickableIcons: true,
                                        scrollwheel: true,
                                        gestureHandling: 'greedy',
                                        disableDefaultUI: true,
                                    }}
                                    onClick={() => {
                                        if (ignoreNextMapClickRef.current) {
                                            ignoreNextMapClickRef.current = false
                                            return
                                        }
                                        setActivePoint(null)
                                    }}
                                >
                                    {points.map((p, idx) => (
                                        <MarkerF
                                            key={p.id ?? `${p.lat}-${p.lng}-${idx}`}
                                            position={{ lat: p.lat as number, lng: p.lng as number }}
                                            onClick={() => {
                                                const newCenter = { lat: p.lat as number, lng: p.lng as number }
                                                setMapCenter(newCenter)
                                                setActivePoint(p)
                                            }}
                                        />
                                    ))}

                                    {activePoint && (
                                        <InfoWindowF
                                            position={{ lat: activePoint.lat as number, lng: activePoint.lng as number }}
                                            onCloseClick={() => {
                                                ignoreNextMapClickRef.current = true
                                                setActivePoint(null)
                                            }}
                                        >
                                            <div
                                                style={{ maxWidth: 320 }}
                                                onMouseDown={() => (ignoreNextMapClickRef.current = true)}
                                                onClick={() => (ignoreNextMapClickRef.current = true)}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                                                    <LocationOnOutlinedIcon fontSize="small" color="action" />
                                                    <Typography fontWeight={700}>
                                                        {activePoint.title ?? 'No title'}
                                                    </Typography>
                                                </Stack>

                                                {activePoint.visitedAt && (
                                                    <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 8 }}>
                                                        Visited: {String(activePoint.visitedAt)}
                                                    </div>
                                                )}

                                                {activePoint.img && (
                                                    <div style={{ marginTop: 6 }}>
                                                        <img
                                                            src={activePoint.img}
                                                            alt={activePoint.title ?? 'place'}
                                                            style={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 8 }}
                                                            loading="lazy"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </InfoWindowF>
                                    )}
                                </GoogleMap>
                            </Box>
                        </Grid>
                    </LoadScript>
                ) : (
                    <Typography fontSize={14} sx={{ wordBreak: 'break-all' }}>
                        No geolocation provided
                    </Typography>
                )}
            </Grid>
        </Stack>
    )
}
